import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icons as I } from '../components/pack-icons';
import { api } from '../services/api/client.js';
import { CLAUDE_INSTALL_URL, MCP_ENDPOINT_URL, writeMcpOnboardingChoice } from '../lib/connectors.js';
import { CopyChip, MiniStepper, StarterPrompt } from './OnboardingMcpShared.jsx';

const POLL_MS = 5000;
const RETRY_AFTER_MS = 3 * 60 * 1000;

export default function OnboardingSayHello() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mountedAt = useRef(Date.now());
  const latestStatus = useRef(null);
  const [state, setState] = useState('waiting');
  const [manual, setManual] = useState(false);

  const poll = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    try {
      const response = await api.apiKeys.connectorsStatus();
      const status = response?.data ?? response;
      latestStatus.current = status;
      if (status?.lastCallAt) setState('connected');
    } catch (_) { /* keep waiting; the next poll retries */ }
  }, []);

  useEffect(() => {
    writeMcpOnboardingChoice('ai');
    poll();
    const timer = setInterval(poll, POLL_MS);
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - mountedAt.current >= RETRY_AFTER_MS && latestStatus.current?.connected === false && !latestStatus.current?.lastCallAt) {
        setState('retry');
      }
      poll();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [poll]);

  const useApp = (event) => {
    event.preventDefault();
    writeMcpOnboardingChoice('ui');
    navigate('/onboarding?setup=ui', { replace: true });
  };
  const retry = () => {
    window.open(CLAUDE_INSTALL_URL, '_blank', 'noopener,noreferrer');
    mountedAt.current = Date.now();
    setState('waiting');
  };

  const connected = state === 'connected';
  const retrying = state === 'retry';
  return (
    <main className="mcpo-stage">
      <section className="mcpo-panel mcpo-hello-panel">
        <MiniStepper active={connected ? 3 : retrying ? 1 : 2} doneThrough={connected ? 2 : retrying ? 0 : 1} />
        <div className="mcpo-hello-hero" role={connected ? 'status' : undefined}>
          {connected ? <span className="mcpo-ok-mark"><I.check size={24} /></span> : retrying ? <span className="mcpo-error-mark"><I.info size={22} /></span> : <span className="mcpo-pulse" role="status" aria-label={t('mcpOnboarding.waitingAria')}><I.message size={22} /></span>}
          <h1>{t(`mcpOnboarding.${connected ? 'connectedTitle' : retrying ? 'retryTitle' : 'waitingTitle'}`)}</h1>
          <p className="mcpo-subtitle">{t(`mcpOnboarding.${connected ? 'connectedSubtitle' : retrying ? 'retrySubtitle' : 'waitingSubtitle'}`)}</p>
        </div>

        <StarterPrompt caption={!connected} />

        {connected && (
          <div className="mcpo-next">
            <p className="mcpo-next-title">{t('mcpOnboarding.nextTitle')}</p>
            <div className="mcpo-next-row"><span className="mcpo-next-icon"><I.globe size={13} /></span>{t('mcpOnboarding.nextSite')}</div>
            <div className="mcpo-next-row"><span className="mcpo-next-icon"><I.search size={13} /></span>{t('mcpOnboarding.nextResearch')}</div>
            <div className="mcpo-next-row"><span className="mcpo-next-icon"><I.calendar size={13} /></span>{t('mcpOnboarding.nextPlan')}</div>
          </div>
        )}

        {retrying && (
          <div className="mcpo-retry-actions">
            <button type="button" className="mcpo-primary" onClick={retry}>{t('mcpOnboarding.retryConnect')}</button>
            <button type="button" className="mcpo-ghost" onClick={() => setManual((open) => !open)} aria-expanded={manual}>{t('mcpOnboarding.showManual')}</button>
          </div>
        )}
        {retrying && manual && (
          <div className="mcpo-manual-note">
            <span>{t('mcpOnboarding.chatgptPaste')}</span>
            <div className="mcpo-command"><code>{MCP_ENDPOINT_URL}</code><CopyChip value={MCP_ENDPOINT_URL} /></div>
          </div>
        )}

        <p className="mcpo-quiet"><a href={connected ? '/app' : '/onboarding'} onClick={connected ? undefined : useApp}>{connected ? t('mcpOnboarding.watchProgress') : t('mcpOnboarding.preferApp')}</a></p>
      </section>
    </main>
  );
}
